import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ProposalBundle, TransportProposalItem } from '../../../types/proposalBundles';
import { getForeignFlightCaptionHint, getForeignFlightDurationHeaderLabel } from '../utils';
import { ForeignFlightProposalTransportTableRow } from './ForeignFlightProposalTransportTableRow';

export const FOREIGN_FLIGHT_COL_WIDTHS = ['3%', '14%', '11%', '10%', '15%', '9%', '6%', '11%', '21%'] as const;

export const FOREIGN_FLIGHT_COL_WIDTHS_COMPACT = [
  '3%',
  '12%',
  '12%',
  '9%',
  '11%',
  '8%',
  '4%',
  '5%',
  '36%'
] as const;

interface ForeignFlightProposalTransportTableProps {
  items: TransportProposalItem[];
  showRoundTripAirPriceHeader: boolean;
  includeTax: boolean;
  onSelect: (itemId: number) => void;
  disabled: boolean;
  hasRoundTripPair: boolean;
  bundleCategory: ProposalBundle['category'];
  routeCaption: string;
  enableHorizontalScroll?: boolean;
}

export const ForeignFlightProposalTransportTable = ({
  items,
  showRoundTripAirPriceHeader,
  includeTax,
  onSelect,
  disabled,
  hasRoundTripPair,
  bundleCategory,
  routeCaption,
  enableHorizontalScroll = false
}: ForeignFlightProposalTransportTableProps) => {
  const { theme } = useAITTheme();
  const tableStyles = useMemo(() => getStyles(theme, enableHorizontalScroll), [theme, enableHorizontalScroll]);
  const colWidths = useMemo(
    () => (enableHorizontalScroll ? FOREIGN_FLIGHT_COL_WIDTHS_COMPACT : FOREIGN_FLIGHT_COL_WIDTHS),
    [enableHorizontalScroll]
  );
  const wrapStyle = useMemo(
    () =>
      enableHorizontalScroll
        ? { ...tableStyles.wrap, minWidth: 0 as const, maxWidth: '100%' as const }
        : tableStyles.wrap,
    [enableHorizontalScroll, tableStyles.wrap]
  );
  const tableScrollStyle = useMemo(
    () =>
      enableHorizontalScroll
        ? {
            ...tableStyles.tableScroll,
            overflowX: 'auto' as const,
            overflowY: 'hidden' as const,
            minWidth: 0
          }
        : tableStyles.tableScroll,
    [enableHorizontalScroll, tableStyles.tableScroll]
  );
  const mergedTableStyle = useMemo(
    () => ({ ...tableStyles.table, ...tableStyles.frTable }),
    [tableStyles.frTable, tableStyles.table]
  );
  const durationHeader = getForeignFlightDurationHeaderLabel(hasRoundTripPair, bundleCategory);
  const hint = getForeignFlightCaptionHint(hasRoundTripPair, bundleCategory);

  return (
    <div style={wrapStyle}>
      {routeCaption.length > 0 ? (
        <div style={tableStyles.captionBlock}>
          <div style={tableStyles.captionRoute}>国際線{routeCaption}</div>
          <div style={tableStyles.captionHint}>{hint}</div>
        </div>
      ) : (
        <div style={tableStyles.captionBlock}>
          <div style={tableStyles.captionRoute}>国際線</div>
          <div style={tableStyles.captionHint}>{hint}</div>
        </div>
      )}
      <div style={tableScrollStyle}>
        <table style={mergedTableStyle}>
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <thead>
            <tr style={tableStyles.tableHeaderRow}>
              <th style={tableStyles.frThNarrow}> </th>
              <th style={tableStyles.frTh}>出発→到着</th>
              <th style={tableStyles.frTh}>{durationHeader}</th>
              <th style={tableStyles.frTh}>区間</th>
              <th style={tableStyles.frTh}>航空会社</th>
              <th style={tableStyles.frTh}>便名</th>
              <th style={tableStyles.frTh}>座席</th>
              <th style={tableStyles.frThCenter}>変更可否</th>
              <th style={tableStyles.frThPrice}>{showRoundTripAirPriceHeader ? '料金（往復）' : '料金'}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <ForeignFlightProposalTransportTableRow
                key={item.id}
                item={item}
                index={index}
                includeTax={includeTax}
                onSelect={() => onSelect(item.id)}
                disabled={disabled}
                hasRoundTripPair={hasRoundTripPair}
                bundleCategory={bundleCategory}
                compactLayout={enableHorizontalScroll}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme, compactHeader: boolean) => ({
  wrap: {
    width: '100%'
  },
  tableScroll: {
    width: '100%',
    maxWidth: '100%'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  frTable: {
    width: '100%',
    maxWidth: '100%',
    tableLayout: 'fixed' as const
  },
  captionBlock: {
    padding: '0 0 10px 0',
    fontSize: '12px',
    lineHeight: 1.5,
    color: theme.grayTextColor ?? '#555555'
  },
  captionRoute: {
    fontWeight: 600 as const,
    color: theme.textColor
  },
  captionHint: {
    marginTop: '4px',
    fontSize: '11px',
    color: theme.grayTextColor ?? '#666666'
  },
  frThNarrow: {
    width: '2.5%',
    padding: '6px 2px',
    fontSize: '10px'
  },
  frTh: {
    padding: compactHeader ? '6px 2px' : '6px 4px',
    textAlign: 'left' as const,
    fontSize: '10px',
    fontWeight: 600 as const,
    whiteSpace: 'nowrap' as const
  },
  frThCenter: {
    padding: compactHeader ? '4px 2px' : '6px 2px 6px 4px',
    textAlign: 'center' as const,
    fontSize: '10px',
    fontWeight: 600 as const,
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
    boxSizing: 'border-box' as const
  },
  frThPrice: {
    padding: compactHeader ? '6px 2px 6px 0' : '6px 4px 6px 2px',
    textAlign: 'right' as const,
    fontSize: '10px',
    fontWeight: 600 as const,
    whiteSpace: 'normal' as const,
    lineHeight: 1.35,
    wordBreak: 'break-word' as const,
    minWidth: 0
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ececec'
  }
});

export const getForeignFlightProposalTransportTableStyles = getStyles;
